import React from 'react';
import { useTranslation } from 'react-i18next';
import './ProjectSummary.css';

export default function ProjectSummary({
  selectedObjective,
  projectName,
  siteUrl,
  selectedWebsite,
  language,
  numberOfTexts,
  projectManager,
  deadline,
  productionMode,
  selectedAgent,
  selectedWriters,
  contentFormat,
  customWordCount,
  contentEnrichment,
  semanticGapLink,
  detailedBriefLink,
  currentStep,
  writerTasks = [],
}) {
  const { t } = useTranslation();

  const formatDateFrench = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = [t('janvier'), t('février'), t('mars'), t('avril'), t('mai'), t('juin'), t('juillet'), t('août'), t('septembre'), t('octobre'), t('novembre'), t('décembre')];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getWordCountRange = (task) => {
    if (task.contentFormat?.isCustom) {
      const min = task.customMinWords || '';
      const max = task.customMaxWords || '';
      if (min && max) {
        return `${min} ${t('à')} ${max} ${t('mots')}`;
      } else if (min) {
        return `${min} ${t('mots minimum')}`;
      } else if (max) {
        return `${max} ${t('mots maximum')}`;
      }
      return '';
    }
    
    if (task.contentFormat) {
      switch (task.contentFormat.type) {
        case 'SHORT_ARTICLE':
          return `300 ${t('mots')}`;
        case 'MEDIUM_ARTICLE':
          return `600 ${t('mots')}`;
        case 'LONG_ARTICLE':
          return `1000 ${t('mots')}`;
        case 'YOUTUBE':
          return `350 ${t('mots')}`;
        case 'LINKEDIN':
          return `200 ${t('mots')}`;
        case 'X':
          return `150 ${t('mots')}`;
        default:
          return '';
      }
    }
    return '';
  };

  const getTotalTexts = () => {
    if ((productionMode === 'redacteur' || productionMode === 'ia-human') && writerTasks.length > 0) {
      return writerTasks.reduce((sum, task) => sum + (parseInt(task.numberOfTexts) || 0), 0);
    }
    return numberOfTexts || 0;
  };

  const hasProductionModeData = () => {
    if (productionMode === 'redacteur') {
      return writerTasks.length > 0 && writerTasks.some(task => task.writer || task.deadline || task.contentFormat || task.numberOfTexts);
    }
    return selectedAgent || contentFormat || contentEnrichment;
  };
  return (
    <div className="project-summary-container">
      <div className={`summary-section ${currentStep > 1 ? 'summary-section-completed' : ''}`}>
        <div className="summary-section-title">→ {t('Objectif')}</div>
        <div className="summary-item">
          {selectedObjective === 'redaction' ? t('Rédaction') : t('Traduction')}
        </div>
        {projectName && (
          <div className="summary-item">{projectName}</div>
        )}
        {siteUrl && (
          <div className="summary-item">
            {t('Site web')} : {selectedWebsite?.urlSite || selectedWebsite?.url || selectedWebsite?.name || siteUrl}
          </div>
        )}
        {selectedWebsite?.language && (
          <div className="summary-item">
            {t('Langue du site')} : {selectedWebsite.language?.language || selectedWebsite.language?.name || ''}
          </div>
        )}
        {numberOfTexts && (
          <div className="summary-item">{numberOfTexts} {t('textes')}</div>
        )}
        {projectManager && (
          <div className="summary-item">
            {t('Chef de projet')} : {projectManager.mail || [projectManager.firstName, projectManager.lastName].filter(Boolean).join(' ') || ''}
          </div>
        )}
        {deadline && (
          <div className="summary-item">{t('Deadline')} : {formatDateFrench(deadline)}</div>
        )}
      </div>
      <div className={`summary-section ${currentStep > 2 ? 'summary-section-completed' : ''}`}>
        <div className="summary-section-title">{t('Mode de production')}</div>
        {currentStep < 2 && !hasProductionModeData() ? (
          <div className="summary-item">{t('À venir')}</div>
        ) : productionMode === 'redacteur' ? (
          <>
            <div className="summary-item">{t('Type')} : {t('Rédacteur')}</div>
            {writerTasks.length > 0 && (
              <>
                <div className="summary-item">
                  {getTotalTexts()} {t('textes')} | {writerTasks.length} {t('Rédacteur')}{writerTasks.length > 1 ? 's' : ''}
                </div>
                {writerTasks.map((task, index) => (
                  <div key={task.id || index} style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
                    <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                      <span>{t('Rédacteur')} #{index + 1} → {task.writer 
                        ? (task.writer?.mail || [task.writer?.firstName, task.writer?.lastName].filter(Boolean).join(' ') || t('Rédacteur'))
                        : t('Moi (par défaut)')}</span>
                    </div>
                    {task.deadline && (
                      <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                        <span>{t('Livraison le')} {formatDateFrench(task.deadline)}</span>
                      </div>
                    )}
                    {getWordCountRange(task) && (
                      <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                        <span>{getWordCountRange(task)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </>
        ) : productionMode === 'ia-human' ? (
          <>
            <div className="summary-item">{t('IA avec validation humaine.')}</div>
            {selectedAgent && (
              <div className="summary-item">
                {t('Agent')} : {selectedAgent.name || selectedAgent.agentName || ''}
              </div>
            )}
            {contentFormat && (
              <div className="summary-item">
                {t('Format')} : {contentFormat.name || ''}
                {contentFormat.isCustom && customWordCount && ` - ${customWordCount} ${t('mots')}`}
              </div>
            )}
            {contentEnrichment && (
              <div className="summary-item">{t('Enrichissement du contenu activé')}</div>
            )}
          </>
        ) : (
          <>
            <div className="summary-item">
              {productionMode === 'ia' && t('Production généré par IA')}
            </div>
            {productionMode === 'ia' && selectedAgent && (
              <div className="summary-item">
                {t('Agent')} : {selectedAgent.name || selectedAgent.agentName || ''}
              </div>
            )}
            {contentFormat && (
              <div className="summary-item">
                {t('Format')} : {contentFormat.name || ''}
                {contentFormat.isCustom && customWordCount && ` - ${customWordCount} ${t('mots')}`}
              </div>
            )}
            {contentEnrichment && (
              <div className="summary-item">{t('Enrichissement du contenu activé')}</div>
            )}
          </>
        )}
      </div>
      {productionMode === 'ia-human' && (
        <div className={`summary-section ${currentStep > 3 ? 'summary-section-completed' : ''}`}>
          <div className="summary-section-title">{t('Répartition du travail')}</div>
          {currentStep < 3 && writerTasks.length === 0 ? (
            <div className="summary-item">{t('À venir')}</div>
          ) : writerTasks.length > 0 ? (
            <>
              <div className="summary-item">
                {getTotalTexts()} {t('textes')} | {writerTasks.length} {t('Relecteur')}{writerTasks.length > 1 ? 's' : ''}
              </div>
              {writerTasks.map((task, index) => (
                <div key={task.id || index} style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
                  <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                    <span>{t('Relecteur')} #{index + 1} → {task.writer 
                      ? (task.writer?.mail || [task.writer?.firstName, task.writer?.lastName].filter(Boolean).join(' ') || t('Relecteur'))
                      : t('Moi (par défaut)')}</span>
                  </div>
                  {task.numberOfTexts && (
                    <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                      <span>{task.numberOfTexts} {parseInt(task.numberOfTexts) > 1 ? t('textes') : t('texte')}</span>
                    </div>
                  )}
                  {task.deadline && (
                    <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                      <span>{t('Livraison le')} {formatDateFrench(task.deadline)}</span>
                    </div>
                  )}
                  {task.modificationLimit && (
                    <div className="summary-item" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ marginRight: '8px', color: '#1A1A1A' }}>•</span>
                      <span>{t('Limite de modification')} : {task.modificationLimit} {t('mots')}</span>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div className="summary-item">{t('Aucune attribution')}</div>
          )}
        </div>
      )}
      <div className={`summary-section ${productionMode === 'ia-human' ? (currentStep > 4 ? 'summary-section-completed' : '') : (currentStep > 3 ? 'summary-section-completed' : '')}`}>
        <div className="summary-section-title">{t('Contexte éditorial')}</div>
        {((productionMode === 'ia-human' && currentStep < 4) || (productionMode !== 'ia-human' && currentStep < 3)) && !semanticGapLink && !detailedBriefLink ? (
          <div className="summary-item">{t('À venir')}</div>
        ) : (
          <>
            {semanticGapLink && (
              <div className="summary-item">
                {t('Gap sémantique')} : {semanticGapLink}
              </div>
            )}
            {detailedBriefLink && (
              <div className="summary-item">
                {t('Brief détaillé')} : {detailedBriefLink}
              </div>
            )}
            {!semanticGapLink && !detailedBriefLink && (
              <div className="summary-item">{t('Aucun élément ajouté')}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

